import React, { useState } from 'react';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AxiosRequest from '../../../../utils/AxiosRequest';
import config       from '../../../../configuration/configuration.js';

// Dependencies: yarn add html2canvas jspdf

export default function Beacon4BlendedExportButton({ containerRef, title = 'Beacon4_MonthlyBlended' }) {
  const [anchor,  setAnchor]  = useState(null);
  const [loading, setLoading] = useState(false);

  const captureAll = async () => {
    const html2canvas = (await import('html2canvas')).default;
    const el = containerRef?.current;
    if (!el) return null;
    return await html2canvas(el, {
      scale:        2,
      useCORS:      true,
      backgroundColor: '#f0f2f5',
      logging:      false,
      allowTaint:   true,
      imageTimeout: 0,
      width:        el.scrollWidth,
      height:       el.scrollHeight,
      windowWidth:  el.scrollWidth,
      windowHeight: el.scrollHeight,
    });
  };

  const captureEachChart = async () => {
    const html2canvas = (await import('html2canvas')).default;
    const el = containerRef?.current;
    if (!el) return [];

    const cards = Array.from(el.querySelectorAll('.MuiPaper-root'))
      .filter(node => node.querySelector('canvas'));

    const results = [];
    for (const card of cards) {
      const titleEl = card.querySelector('[class*="MuiTypography"]');
      const name    = titleEl?.textContent?.trim() ?? 'KPI';
      const canvas  = await html2canvas(card, {
        scale: 2, useCORS: true,
        backgroundColor: '#ffffff',
        logging: false, allowTaint: true, imageTimeout: 0,
      });
      results.push({ name, imageBase64: canvas.toDataURL('image/jpeg', 0.95) });
    }
    return results;
  };

  const exportPDF = async () => {
    setAnchor(null); setLoading(true);
    try {
      const { jsPDF } = await import('jspdf');
      const charts    = await captureEachChart();
      if (!charts.length) return;

      const pdf     = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw      = pdf.internal.pageSize.getWidth();
      const ph      = pdf.internal.pageSize.getHeight();
      const margin  = 12;
      const spacing = 8;
      const usableW = pw - margin * 2;
      const usableH = ph - margin * 2 - (charts.length - 1) * spacing - 6;
      const hPer    = usableH / charts.length;

      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 120);
      pdf.text(title, pw / 2, margin / 2 + 2, { align: 'center' });

      charts.forEach((chart, i) => {
        const props = pdf.getImageProperties(chart.imageBase64);
        const ratio = props.width / props.height;
        const w     = Math.min(usableW, hPer * ratio);
        const h     = w / ratio;
        const x     = margin + (usableW - w) / 2;
        const y     = margin + 4 + i * (hPer + spacing);
        pdf.addImage(chart.imageBase64, 'JPEG', x, y, w, h, '', 'FAST');
      });

      pdf.save(`${title}.pdf`);
    } finally { setLoading(false); }
  };

  const exportPNG = async () => {
    setAnchor(null); setLoading(true);
    try {
      const canvas = await captureAll();
      if (!canvas) return;
      const link    = document.createElement('a');
      link.download = `${title}.png`;
      link.href     = canvas.toDataURL('image/png');
      link.click();
    } finally { setLoading(false); }
  };

  const exportPPT = async () => {
    setAnchor(null); setLoading(true);
    try {
      const charts = await captureEachChart();
      if (!charts.length) return;

      const res = await AxiosRequest({
        method:       'POST',
        url:          `${config.API_URL}Beacon4Export/Pptx`,
        data:         { title, charts },
        responseType: 'blob',
      });

      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `${title}.pptx`;
      link.click();
      URL.revokeObjectURL(url);
    } finally { setLoading(false); }
  };

  return (
    <>
      <Button
        variant="outlined" size="small"
        onClick={e => setAnchor(e.currentTarget)}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={12} /> : <DownloadIcon sx={{ fontSize: 14 }} />}
        sx={{ height: 30, fontSize: 11, fontWeight: 600, textTransform: 'none', px: 1.5, borderColor: '#cdd5e0', color: '#4a5568', '&:hover': { borderColor: '#1565c0', color: '#1565c0' } }}>
        {loading ? 'Exporting...' : 'Export'}
      </Button>

      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,.12)', minWidth: 200 } }}>
        <MenuItem onClick={exportPDF} sx={{ fontSize: 12, gap: 1.5, py: 1 }}>
          <Box sx={{ width: 22, height: 22, bgcolor: '#c53030', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PDF</Typography>
          </Box>
          Export as PDF
        </MenuItem>
        <MenuItem onClick={exportPPT} sx={{ fontSize: 12, gap: 1.5, py: 1 }}>
          <Box sx={{ width: 22, height: 22, bgcolor: '#d04423', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PPT</Typography>
          </Box>
          Export as PowerPoint
        </MenuItem>
        <MenuItem onClick={exportPNG} sx={{ fontSize: 12, gap: 1.5, py: 1 }}>
          <Box sx={{ width: 22, height: 22, bgcolor: '#3182ce', borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 8, color: '#fff', fontWeight: 700 }}>PNG</Typography>
          </Box>
          Export as Image
        </MenuItem>
      </Menu>
    </>
  );
}